import React from 'react'
import * as blueprint from '@adc/blueprint'
import {
  PopupAddress,
  AuctionInfo,
  PropertyInfo,
  PropertyValue,
  ListingCardLinkProps,
  CardImage,
} from './cardParts'
import DualTrackLobIndicators, {
  buildSiblingListingPaths,
} from '../dualTrackLobIndicators'
// @ts-ignore no types
import analytics from '@adc/analytics'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import { formatListingImgUrl } from '../utils/formatters'
import classnames from 'classnames/bind'
import cls from './styles.scss'
import { getListingImageUrl } from '../../../utils/listing'
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback'
import type { Listing } from '@adc/sdk/types'

const { getCurrentBid } = blueprint.listing.utils.card

const cx = classnames.bind(cls)

const LISTING_CARD_PHOTO_WIDTH = 110

const getListingPhotoUrl = (
  listingPrimaryPhoto: string | null,
  multiplier = 1
): string =>
  formatListingImgUrl(listingPrimaryPhoto, {
    width: LISTING_CARD_PHOTO_WIDTH * multiplier,
  })

interface ListingPopupCardProps {
  listing: Listing
  elmId?: string
  className?: string
  linkProps?: ListingCardLinkProps
  onMouseOver?: (e: React.MouseEvent) => void
  onMouseOut?: (e: React.MouseEvent) => void
  isCancelled?: boolean
  featureFlags?: Record<string, any>
}

interface AnalyticsAttributes {
  badges: string[]
  isReserveMetShown: boolean
  displayedCurrentBid: number | null | undefined
  displayedERV: number | null | undefined
}

const ListingPopupCard = ({
  listing,
  elmId,
  className,
  linkProps,
  onMouseOver,
  onMouseOut,
  isCancelled = false,
  featureFlags = {},
}: ListingPopupCardProps) => {
  const onMouseClick = () => {
    if (!isCancelled && linkProps?.onClick) {
      linkProps.onClick({
        ...analytics.standardTracking.listing(listing),
        ...getAnalyticsAttributes(listing),
      })
    }
  }

  const getAnalyticsAttributes = (listing: Listing): AnalyticsAttributes => ({
    badges: [],
    displayedCurrentBid: getCurrentBid(listing),
    displayedERV: null,
    isReserveMetShown: false,
  })

  const primaryPhoto = useBackgroundImageWithFallback(
    listing.primary_photo,
    featureFlags['fe.resi.enabled-photo-enhancement']
  )

  const listingPhotoUrl = getListingPhotoUrl(primaryPhoto, 2)

  const isDualTrack = buildSiblingListingPaths(listing).length > 1

  return (
    <div
      className={cx(
        'popup-card',
        { cancelled: isCancelled },
        {
          seen: listing.primary_property?.current_user_tracking_state?.is_seen,
        },
        { 'dual-track': isDualTrack },
        className
      )}
      onClick={onMouseClick}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseOut}
      data-elm-id={elmId}
    >
      <CardImage
        listing={listing}
        listingPhotoUrl={listingPhotoUrl}
        defaultPhotoUrl={getListingImageUrl()}
      />
      <div className={cx('info-section')}>
        <PropertyValue listing={listing} isPopup featureFlags={featureFlags} />
        <div className={cx('popup-info-address')}>
          <PropertyInfo
            listing={listing}
            elmId={elmId}
            className={cx('popup-property-info')}
            isCancelled={isCancelled}
          />
          {!isCancelled &&
            listing.formatted_address &&
            listing.formatted_address.length >= 2 && (
              <PopupAddress address={listing.formatted_address} elmId={elmId} />
            )}
        </div>
        {isDualTrack ? (
          <DualTrackLobIndicators
            listing={listing}
            elmId={elmId}
            direction='column'
            bold
            className={cx('popup-auction-info')}
          />
        ) : (
          <AuctionInfo
            listing={listing}
            elmId={elmId}
            className={cx('popup-auction-info')}
          />
        )}
      </div>
    </div>
  )
}

// @ts-ignore next line
ListingPopupCard[GRAPH_API_FRAGMENT_DEFINITION] = `
  fragment ListingPopupCardFields on Listing {
    listing_id
    formatted_address
    listing_summary {
      is_remote_bid_enabled
      is_remote_before_and_during_auction_enabled
      show_opening_bid
    }
    listing_status_group
    listing_page_path
    primary_photo
    reserve_price

    listing_configuration {
      product_type
      asset_type
      is_third_party_online
    }

    # TEMPORARY dual-track dedupe metadata (search-only).
    dual_track_sibling_lobs {
      listing_id
      product_type
      asset_type
    }

    event {
      event_code
    }

    seller_property {
      street_description
      country_secondary_subdivision
      municipality
      postal_code
    }

    program_configuration {
      program_enrollment_code
    }

    primary_property {
      property_id
      summary {
        total_bedrooms
        total_bathrooms
        square_footage
        address {
          coordinates {
            lat
            lon
          }
        }
      }
      is_newly_listed
      current_user_tracking_state {
        is_seen
        is_updated
      }
    }

    auction {
      starting_bid
      is_online
      bid_instruction {
        nos_amount
      }
    }

    selling_method(resolvePolicy: CACHE_ONLY) {
      __typename
      current_highest_bid {
        bid_amount
      }
    }
  }
`

// @ts-ignore next line
ListingPopupCard[GRAPH_API_FRAGMENT_REFERENCE] = `...ListingPopupCardFields`

export default ListingPopupCard
